import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatDateTime } from '../lib/format'
import type { Agent, AgentUpdateStand } from '../lib/types'

/** Texte der Update-Stände, wie Agent und Update-Dienst sie melden (Stufe 4.1). */
export const UPDATE_STAENDE: Record<string, string> = {
  wartet: 'geplant',
  gesendet: 'angeboten',
  bereitgestellt: 'geprüft, wird installiert',
  aktuell: 'bereits aktuell',
  installiert: 'installiert',
  abgelehnt: 'abgelehnt',
  fehlgeschlagen: 'fehlgeschlagen',
  zurueckgerollt: 'zurückgerollt',
  ohne_helfer: 'Update-Dienst fehlt',
}

const FEHLER = new Set(['abgelehnt', 'fehlgeschlagen', 'zurueckgerollt', 'ohne_helfer'])

/** Zeile unter der Version eines Agents: wie weit sein Update ist. */
export function updateHinweis(agent: Agent): { text: string; fehler: boolean } | null {
  if (!agent.update_state) return null
  const stand = UPDATE_STAENDE[agent.update_state] ?? agent.update_state
  const ziel = agent.update_target ? `→ ${agent.update_target}: ` : ''
  const grund = agent.update_message ? ` (${agent.update_message})` : ''
  return { text: `${ziel}${stand}${grund}`, fehler: FEHLER.has(agent.update_state) }
}

export function useAgentUpdateStand() {
  return useQuery({
    queryKey: ['agent-update'],
    queryFn: ({ signal }) => apiGet<AgentUpdateStand>('/agent-update', signal),
    refetchInterval: 15_000,
  })
}

/**
 * Selbstaktualisierung der Agents: welche signierte Version bereitsteht und
 * wie weit das Ausrollen ist. Aktualisiert wird in Wellen; scheitert ein
 * Agent, hält das Ausrollen an.
 */
export function AgentSelbstaktualisierung({ kundeId, editable }: { kundeId?: string; editable: boolean }) {
  const queryClient = useQueryClient()
  const stand = useAgentUpdateStand()
  const neu = () => {
    void queryClient.invalidateQueries({ queryKey: ['agent-update'] })
    void queryClient.invalidateQueries({ queryKey: ['agents'] })
  }
  const alle = useMutation({
    mutationFn: () =>
      apiSend<{ geplant: number; version: string }>('POST', '/agent-update', kundeId ? { kunde_id: kundeId } : {}),
    onSuccess: neu,
  })
  const anhalten = useMutation({
    mutationFn: () => apiSend<{ angehalten: number }>('DELETE', '/agent-update'),
    onSuccess: neu,
  })

  const d = stand.data
  // Eine Antwort ohne die erwartete Form (ältere Zentrale) lässt den Bereich weg.
  if (!d || !d.release || !Array.isArray(d.release.architekturen)) return null
  const r = d.release
  const laeuft = d.geplant + d.laufend > 0

  return (
    <div className="card-body stack" style={{ gap: 8, borderBottom: '1px solid var(--border)' }}>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>Selbstaktualisierung</strong>
        {r.bereit ? (
          <span className="muted" style={{ fontSize: 13 }}>
            Version <span className="mono">{r.version}</span> signiert
            {r.signiert_am ? ` am ${formatDateTime(r.signiert_am)}` : ''} · {d.veraltet}{' '}
            {d.veraltet === 1 ? 'Agent veraltet' : 'Agents veraltet'}
            {laeuft ? ` · ${d.geplant} geplant, ${d.laufend} laufend` : ''}
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 13 }}>
            {r.grund || 'Kein signiertes Update vorhanden.'}
          </span>
        )}
        {editable && r.bereit ? (
          <span className="row" style={{ gap: 8, marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={alle.isPending || d.veraltet === 0}
              onClick={() => {
                if (window.confirm(`Alle veralteten Agents auf ${r.version} aktualisieren? Das geschieht in Wellen.`)) {
                  alle.mutate()
                }
              }}
            >
              Alle aktualisieren
            </button>
            {laeuft || d.fehlgeschlagen > 0 ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={anhalten.isPending}
                onClick={() => anhalten.mutate()}
              >
                Ausrollen anhalten
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      {d.fehlgeschlagen > 0 ? (
        <span style={{ color: 'var(--crit)', fontSize: 13 }}>
          Ausgerollt wird nicht weiter: {d.fehlgeschlagen} {d.fehlgeschlagen === 1 ? 'Update ist' : 'Updates sind'}{' '}
          gescheitert. Der Grund steht beim Agent; „Ausrollen anhalten“ beendet den Plan, danach lässt er sich neu
          starten.
        </span>
      ) : null}
      {alle.isError || anhalten.isError ? (
        <span style={{ color: 'var(--crit)', fontSize: 13 }}>{((alle.error ?? anhalten.error) as Error).message}</span>
      ) : null}
    </div>
  )
}

/** Knopf je Agent: nur dieser eine auf die signierte Version. */
export function AgentAktualisieren({ agent, stand }: { agent: Agent; stand?: AgentUpdateStand }) {
  const queryClient = useQueryClient()
  const plan = useMutation({
    mutationFn: () => apiSend<{ geplant: number }>('POST', `/agents/${agent.id}/update`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['agents'] })
      void queryClient.invalidateQueries({ queryKey: ['agent-update'] })
    },
  })
  const r = stand?.release
  if (!r?.bereit || agent.version === r.version || agent.status === 'revoked') return null
  if (agent.update_state === 'wartet' || agent.update_state === 'gesendet' || agent.update_state === 'bereitgestellt') {
    return null
  }
  if (!r.architekturen.includes(agent.arch)) return null
  return (
    <button type="button" className="btn btn-sm" disabled={plan.isPending} onClick={() => plan.mutate()}>
      Auf {r.version} aktualisieren
    </button>
  )
}
